import React, { useEffect, useState } from 'react';
import { useAccountStore } from '@components/Stores/accountStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { DateValueInputWithDateSwitch } from '@components/UI-Elements/Charts/DateValueInputMultiChart/DateValueInputWithDateSwitch';
import { ViewDocumentOverlay } from '@components/UI-Elements/ViewDocumentOverlay';

import { getFileByUrl } from '@sp-firebase/storageFB';
import { IFutureOrder, ISupplierExposure } from '@sp-types/FutureOrderType';
import { getProductNameById, Product } from '@sp-types/productType';
import { getHighestAbsoluteValue, MonthYearValue } from '@utils/MonthYear/MonthYearValue';
import { getLastDate, updateFutureOrderValueCalendars } from '../../AddNewFutureOrderUtils';
import {
  getStartingDate,
  makeNegative,
  mapMaterialToCharts,
} from '../../FOIndexedClientExposure/FillOrderAdjustmentsUtils';
import { MaterialBreakdownSummary } from '../../MaterialBreakdownSummery/MaterialBreakdownSummery';
import { accumulateMaterialExposure } from './BackToBackOrderSummeryUtils';
import { BackToBackFutureOrderSummeryDataSection } from './BackToBackSummeryDataSection/BackToBackSummeryDataSection';
import { FOB2BButtonSection } from './FOB2BButtonSection';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { useUser } from '@hooks/useUser';

interface Props {
  currentFutureOrder: IFutureOrder;
  products: Product[];
  setFutureOrderProperty: <K extends keyof IFutureOrder>(_property: K, _value: IFutureOrder[K]) => void;
  routeToModifyBackToBack: () => void;
  routeToBackPage: () => void;
}

export const FOB2BSummary: React.FC<Props> = ({
  currentFutureOrder,
  products,
  setFutureOrderProperty,
  routeToModifyBackToBack,
  routeToBackPage,
}) => {
  const { account, formulaColorDictionary } = useAccountStore();
  const { data: user } = useUser();
  const { formulasToAccount } = account;
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();

  const { orderId, orderDetails, productId, fileUrl, clientName, status } = currentFutureOrder;
  const { quantity, pricePerUnit, productDeliveryCalendar } = orderDetails;
  const [showOverlay, setShowOverlay] = useState(false);
  const [fileDBUrl, setFileDBUrl] = useState('');

  useEffect(() => {
    const fetchDocument = async () => {
      if (fileUrl.length > 0) {
        const docUrl = await getFileByUrl(fileUrl[0]);
        if (typeof docUrl === 'string') {
          setFileDBUrl(docUrl);
        }
      }
    };
    fetchDocument();
  }, [fileUrl]);

  const supplierToMonthExposure = (supplier: ISupplierExposure[]): Record<string, MonthYearValue[]> =>
    supplier.reduce((acc, { formulaId, exposureCalendar }) => {
      acc[formulaId] = exposureCalendar;
      return acc;
    }, {} as Record<string, MonthYearValue[]>);

  useEffect(() => {
    const { supplierExposure } = updateFutureOrderValueCalendars(
      currentFutureOrder,
      products,
      materialsMap,
      'BackToBack',
      0,
    );

    const currDate = new Date();
    // TODO: move the calc of total exposure to calendar caluclations
    const totalExposure = accumulateMaterialExposure(supplierExposure, {
      month: currDate.getMonth(),
      year: currDate.getFullYear(),
    });
    setFutureOrderProperty('totalExposure', totalExposure);
    setFutureOrderProperty('monthExposure', supplierToMonthExposure(supplierExposure));
  }, [currentFutureOrder.orderDetails, currentFutureOrder.updatedAt]);

  return (
    currentFutureOrder?.totalExposure && (
      <div className="flex flex-col flex-1 pt-2 overflow-x-hidden">
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden py-2">
          <div>
            <BackToBackFutureOrderSummeryDataSection
              orderId={orderId}
              pricePerUnit={pricePerUnit || 0}
              productName={getProductNameById(productId, products)}
              quantity={quantity}
              clientName={clientName}
            />
          </div>
          <div className="flex flex-col flex-[0.25] py-1 /flexiblePlaceholder/"></div>

          <div className="max-w-[70rem]">
            <MaterialBreakdownSummary
              materialTotalExposure={currentFutureOrder.totalExposure}
              formulasToAccount={formulasToAccount}
              totalQuantity={quantity}
              materialExposureData={currentFutureOrder.supplierExposure}
              formulaColorDictionary={formulaColorDictionary}
              materialsMap={materialsMap}
            />
          </div>
          <div className="flex flex-col flex-1 /flexiblePlaceholder/"></div>

          <div>
            {productDeliveryCalendar && (
              <DateValueInputWithDateSwitch
                mainChart={{
                  data: makeNegative(
                    productDeliveryCalendar.map((item) =>
                      pricePerUnit ? { ...item, value: item.value * pricePerUnit || 1 } : item,
                    ) || [],
                  ),
                  title: 'Product price',
                  type: 'bar',
                  transparent: true,
                }}
                isHideValueInput
                numberOfMonthsToShow={24}
                changeMonths={12}
                additionalCharts={mapMaterialToCharts(
                  currentFutureOrder.totalExposure?.map(({ formulaId, exposureCalendar }) => ({
                    formulaId,
                    exposureCalendar: exposureCalendar.map((dateValue) => ({
                      ...dateValue,
                      value: dateValue.value * pricesMap[formulaId],
                    })),
                  })) || ([] as any),
                  materialsMap,
                  formulaColorDictionary,
                )}
                startingDate={getStartingDate()}
                endDate={orderDetails.productDeliveryCalendar && getLastDate(orderDetails.productDeliveryCalendar)}
                suggestedMaxValue={
                  productDeliveryCalendar &&
                  getHighestAbsoluteValue(productDeliveryCalendar) * 1.2 * (pricePerUnit || 1)
                }
                suggestedMinValue={
                  productDeliveryCalendar &&
                  -getHighestAbsoluteValue(productDeliveryCalendar) * 1.2 * (pricePerUnit || 1)
                }
              />
            )}
          </div>
        </div>
        <div className="flex justify-between pt-2">
          <FOB2BButtonSection
            isEditable={status !== 'completed'}
            backPage={routeToBackPage}
            fileDBUrl={fileDBUrl}
            routeToModifyBackToBack={routeToModifyBackToBack}
            setShowOverlay={setShowOverlay}
            isEditEnabled={!!user?.isEditEnabled}
          />
        </div>
        {showOverlay && fileDBUrl && (
          <ViewDocumentOverlay fileUrl={fileDBUrl} uploadedDocumentPath={fileUrl[0]} setShowOverlay={setShowOverlay} />
        )}
      </div>
    )
  );
};
